import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { useScenarioStore } from '../../pages/scenario-modeling/scenario-modeling.store';
import { ApiService } from '../../services/index';
import {
  LiquidityCapTableByStakeholder,
  LiquidityCapTableByStakeholderType,
  ScenarioCapTableByStakeholder,
  ScenarioCapTableByStakeholderType,
  ScenarioType,
} from '../../types/scenario-modeling.types';
import { useAppQuery } from '../use-app-query.hook';

export type FundraisingCapTableByStakeholderAll = {
  stakeholders: ScenarioCapTableByStakeholder[];
} & ScenarioCapTableByStakeholderType;

export type LiquidityCapTableByStakeholderAll = {
  stakeholders: LiquidityCapTableByStakeholder[];
} & LiquidityCapTableByStakeholderType;

export const useCapTableStakeholderByAll = <
  T extends ScenarioType = ScenarioType.FUNDRAISING,
>() => {
  const queryClient = useQueryClient();
  const { scenarioId, search } = useScenarioStore();

  const { data, isLoading } = useAppQuery<
    (T extends ScenarioType.FUNDRAISING
      ? FundraisingCapTableByStakeholderAll
      : LiquidityCapTableByStakeholderAll)[]
  >({
    queryKey: [QueryKey.CAP_TABLE_STAKEHOLDER_BY_ALL, scenarioId, search],
    queryFn: () =>
      ApiService.get(
        {
          endpoint: BackendRoute.SCENARIOS,
          routePath: [scenarioId, 'cap-table', 'stakeholder-all'],
        },
        { queryParams: { search } },
      ),
    refetchOnWindowFocus: false,
    // Tab switches inside the cap-table sheet remount this query; without staleTime the
    // remount refetch replaces rendered rows with whatever the server returns mid-recalc.
    // Mutations still invalidate explicitly (useCalcCapTable, search handlers).
    staleTime: 30_000,
    enabled: !!scenarioId,
  });

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.CAP_TABLE_STAKEHOLDER_BY_ALL] });
  }, [queryClient]);

  return {
    invalidateQuery,
    data,
    isLoading,
    isEmptySearch: Boolean(search && !data?.length),
  };
};
